// Bridge ke Capacitor native plugin. No-op aman kalau app dibuka di browser biasa
// (Capacitor.isNativePlatform() = false), jadi boleh dipanggil dari komponen web apa pun.
"use client";

import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { PushNotifications } from "@capacitor/push-notifications";
import { NativeBiometric } from "capacitor-native-biometric";
import { App } from "@capacitor/app";
import { setCachedFcmToken } from "@/lib/firebase/client";

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

export async function shareLink(title: string, url: string) {
  if (isNativeApp()) {
    await Share.share({ title, url });
    return;
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    await navigator.share({ title, url });
    return;
  }
  await navigator.clipboard.writeText(url);
}

/**
 * Native push path — replaces web requestFcmToken() when running inside the
 * Capacitor app. Registers with FCM via the native SDK (google-services.json,
 * see apps/android-shell) and feeds the token into the same cache the web
 * path uses, so build-client.ts / use-project.ts pick it up unchanged.
 * No-op outside the native app.
 */
export async function initNativePush(): Promise<void> {
  if (!isNativeApp()) return;
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return;
  PushNotifications.addListener("registration", (token) => setCachedFcmToken(token.value));
  PushNotifications.addListener("registrationError", (err) =>
    console.error("push registration failed", err)
  );
  await PushNotifications.register();
}

/** True if device has usable biometric hardware (fingerprint/face) enrolled. No-op (false) outside the native app. */
export async function biometricAvailable(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const r = await NativeBiometric.isAvailable();
    return r.isAvailable;
  } catch {
    return false;
  }
}

/** Prompts the OS biometric dialog. Returns true on success, false on cancel/failure. No-op (true) outside the native app — never blocks the web flow. */
export async function biometricUnlock(reason = "Buka aplikasi"): Promise<boolean> {
  if (!isNativeApp()) return true;
  try {
    await NativeBiometric.verifyIdentity({ reason, title: "Verifikasi" });
    return true;
  } catch {
    return false;
  }
}

/**
 * Android hardware back button: go back through the in-app navigation the
 * user actually did this session, then exit once there's nowhere left to go.
 *
 * `window.history.length` is NOT usable for this — it counts every entry in
 * the WebView's session history including the ones Next.js's router pushes
 * during initial load, so it's already >1 before the user navigates anywhere
 * and never drops back to 1, meaning exitApp() would never fire. Instead we
 * track our own depth: starts at 0 on app launch, +1 on every route push,
 * -1 on every pop (including the ones we trigger ourselves below).
 */
let backDepth = 0;

export function initBackButtonHandler(): void {
  if (!isNativeApp()) return;

  window.addEventListener("popstate", () => {
    backDepth = Math.max(0, backDepth - 1);
  });

  const originalPushState = window.history.pushState.bind(window.history);
  window.history.pushState = (...args) => {
    backDepth += 1;
    return originalPushState(...args);
  };

  App.addListener("backButton", () => {
    if (backDepth > 0) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });
}
